/**
 * Phase 70 — Personal AI helper.
 *
 * Cross-module types for the on-device WebLLM helper. The helper runs
 * entirely client-side via WebGPU + @mlc-ai/web-llm. No prompts ever
 * leave the device unless the user explicitly opts into a BYO-key tier
 * (deferred to a follow-up; not in the scaffold).
 *
 * The default model is Phi-3.5 mini (~2.4 GB precache). Other model
 * choices are deferred to the follow-up tier configuration surface.
 */

export type HelperState =
  /** No model loaded; first-load CTA visible. */
  | { kind: 'idle' }
  /** Downloading / compiling model weights. progress is 0..1. */
  | { kind: 'loading'; progress: number; status: string }
  /** Model ready; can take prompts. */
  | { kind: 'ready' }
  /** Currently generating a response token-by-token. */
  | { kind: 'generating' }
  /** Last load or generate attempt failed; user can retry. */
  | { kind: 'error'; message: string }
  /** Browser lacks WebGPU; helper cannot run on-device. */
  | { kind: 'unsupported'; reason: string };

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
  ts: string;
}

export interface HelperConfig {
  /** Model identifier per @mlc-ai/web-llm's prebuilt catalog. */
  modelId: string;
  /** Optional override for the system prompt prepended to every chat. */
  systemPrompt?: string;
}

export const DEFAULT_HELPER_CONFIG: HelperConfig = {
  modelId: 'Phi-3.5-mini-instruct-q4f16_1-MLC',
  systemPrompt:
    'You are an on-device helper inside ALDS, a citizen mutual-aid dispatch app. ' +
    'You never see or transmit private user data; everything you know comes from this conversation. ' +
    'Be concise, kind, and practical. If you do not know something, say so.',
};
